import { useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { usePrevNextButtons } from "./SliderArrowBtn";

type Props = {
  children: React.ReactNode;
  /** 要確保在畫面裡的 slide 索引（選中的書），不在畫面裡就捲過去；kanban 1000823 */
  scrollToIndex?: number;
};

export const BookCarousel = ({ children, scrollToIndex }: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    slidesToScroll: "auto",
    dragFree: false,
  });

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [emblaApi, children]);

  // 選中的書不在畫面裡時捲過去（同 Slider）；使用者自己點的本來就在畫面裡，不會多跳
  useEffect(() => {
    if (!emblaApi || scrollToIndex === undefined || scrollToIndex < 0) return;
    if (emblaApi.slidesInView().includes(scrollToIndex)) return;
    const snap = emblaApi.internalEngine().slideRegistry.findIndex((group) => group.includes(scrollToIndex));
    if (snap >= 0) emblaApi.scrollTo(snap);
  }, [emblaApi, scrollToIndex, children]);

  const allFit = prevBtnDisabled && nextBtnDisabled;

  return (
    <div className="hidden md:block relative w-full">
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex gap-6 py-3 px-2">{children}</div>
      </div>
      {!allFit && (
        <>
          <button
            type="button"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
            aria-label="上一頁"
            className="absolute -left-12 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-[#2781E2] shadow-md text-white hover:scale-110 transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <svg viewBox="0 0 532 532" className="w-4 h-4" aria-hidden="true">
              <path
                fill="currentColor"
                d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
            aria-label="下一頁"
            className="absolute -right-12 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-[#2781E2] shadow-md text-white hover:scale-110 transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <svg viewBox="0 0 532 532" className="w-4 h-4" aria-hidden="true">
              <path
                fill="currentColor"
                d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
              />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};
